import { type Club } from '@/types/club.model';
import type { LucideIcon } from 'lucide-react';
import React from 'react';
import { ClubRow } from './ClubRow';

interface ClubSectionProps {
  title: string;
  icon: LucideIcon;
  /** Role accent color — tints the icon box and the count badge. */
  accent: string;
  note: string;
  clubs: Club[];
  role: 'owner' | 'future_owner' | 'manager' | 'instructor';
  /** Forwarded to each row so the list can refresh after a mutation. */
  onClubChanged?: () => void;
}

export const ClubSection: React.FC<ClubSectionProps> = ({
  title,
  icon: Icon,
  accent,
  note,
  clubs,
  role,
  onClubChanged,
}) => {
  return (
    <section>
      {/* Heading row — role differentiation lives here, not on the cards */}
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px]"
          style={{
            background: `color-mix(in srgb, ${accent} 14%, #ffffff)`,
            color: accent,
          }}
        >
          <Icon size={15} />
        </span>
        <span className="text-[15px] font-bold text-gc-ink">{title}</span>
        <span
          className="inline-flex items-center rounded-full px-2 py-px text-[12.5px] font-bold tabular-nums"
          style={{
            background: `color-mix(in srgb, ${accent} 14%, #ffffff)`,
            color: `color-mix(in srgb, ${accent} 78%, #000)`,
          }}
        >
          {clubs.length}
        </span>
        <span className="text-[12.5px] text-gc-muted2">{note}</span>
      </div>

      {/* Mobile: separate cards. Desktop: a single bordered table of rows. */}
      <div className="flex flex-col gap-3 md:gap-0 md:overflow-hidden md:rounded-[14px] md:border md:border-gc-line md:bg-white">
        {clubs.map((club, i) => (
          <ClubRow
            key={club.id}
            club={club}
            role={role}
            isFirst={i === 0}
            onClubChanged={onClubChanged}
          />
        ))}
      </div>
    </section>
  );
};
